import React, { useState, useEffect } from "react"
import { makeStyles } from "@mui/styles"
import MachineColumn from "./MachineColumn"
import TimeLabelsColumn from "./TimeLabelsColumn"
import { resizeAndShiftTiles } from "../../actions/ProdPlanner/prodPlanner"
import { getMachinesByKitchenAreaIdSplitByType } from "../../parseManager/machines/machinesManager"
import CircularProgress from "@mui/material/CircularProgress"

/**
 * Production planner table showing time labels and machine columns
 * Handles tile positioning and resizing within the schedule
 */
const ProdPlannerTable = ({
  tiles = [],
  setTiles,
  previewTiles = [],
  dragging = false,
  kitchenAreaId,
  isAnyTileDragging = false,
  isLoading= false,
  onLinkedMachinesReady,
  onManualResize,
}) => {
  const classes = useStyles()
  const [cookingMachines, setCookingMachines] = useState([])
  const [coolingMachines, setCoolingMachines] = useState([])
  const [allMachines, setAllMachines] = useState([])
  const [isSplitView, setIsSplitView] = useState(false)

  /**
   * Fetches machine names for the kitchen area
   * For "Prépa chaude", splits into cooking and cooling machines
   */
  useEffect(() => {
    if (!kitchenAreaId) return
    
    getMachinesByKitchenAreaIdSplitByType(kitchenAreaId)
      .then((result) => {
        if (result.cookingMachines && result.coolingMachines) {
          setCookingMachines(result.cookingMachines)
          setCoolingMachines(result.coolingMachines)
          setIsSplitView(true)

          if (onLinkedMachinesReady) {
            const linked = {}
            for (const cm of result.cookingMachines) {
              const coolingId = cm.linkedMachine?.objectId
              const coolingMachine = result.coolingMachines.find(m => m.objectId === coolingId)
              if (coolingId && coolingMachine) {
                linked[cm.objectId] = { id: coolingId, name: coolingMachine.name }
              }
            }
            onLinkedMachinesReady(linked)
          }
        } else if (result.allMachines) {
          setAllMachines(result.allMachines)
          setIsSplitView(false)
        }
      })
      .catch((error) => console.error("Error fetching machines:", error))
  }, [kitchenAreaId])

  /**
   * Handles tile resize with conflict resolution
   */
  const handleResize = (tileId, changes) => {
    if (onManualResize) onManualResize(tileId)
    setTiles((prev) => resizeAndShiftTiles(prev, tileId, changes))
  }

  /**
   * Renders a table section with time labels and machine columns
   */
  const renderTableSection = (machines, titleLabel) => (
    <div className={classes.tableSection}>
      {titleLabel && <div className={classes.sectionTitle}>{titleLabel}</div>}
      <div className={classes.paddingWrapper}>
        <div
          className={classes.innerWrapper}
          style={{ pointerEvents: dragging ? "none" : "auto" }}
        >
          <TimeLabelsColumn />
          <div className={classes.columnsWrapper}>
            {machines.map((machine, idx) => {
              return (
                <MachineColumn
                  key={machine.objectId}
                  machineId={machine.objectId}
                  machineIndex={idx}
                  machineLabel={machine.name}
                  machineLength={machines.length}
                  tiles={tiles}
                  previewTiles={previewTiles.filter((s) => s.machine === machine.objectId)}
                  setTiles={setTiles}
                  onResize={handleResize}
                  isAnyTileDragging={isAnyTileDragging}
                />
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <div className={classes.root}>
      {isLoading ? (
        <div style={{ padding: 32, textAlign: "end" }}>
          <CircularProgress />
        </div>
      ) : isSplitView ? (
        <>
          {renderTableSection(cookingMachines, "Plan des sauteuses et fours")}
          {renderTableSection(coolingMachines, "Plan des cellules de refroidissement")}
        </>
      ) : (
        renderTableSection(allMachines, "Plan des machines")
      )}
    </div>
  )
}

export default ProdPlannerTable

const useStyles = makeStyles(() => ({
  root: {
    overflowX: "auto",
    height: "100%",
  },
  tableSection: {
    marginBottom: "40px",
    "&:last-child": {
      marginBottom: 0,
    },
  },
  sectionTitle: {
    paddingLeft: "73px",
    paddingTop: "40px",
    fontSize: "20px",
    fontWeight: 500,
    lineHeight: "28px",
  },
  paddingWrapper: {
    padding: "40px",
    paddingTop: "20px",
    paddingBottom: "40px",
    boxSizing: "border-box",
  },
  innerWrapper: {
    display: "flex",
  },
  columnsWrapper: {
    display: "flex",
    flex: 1, 
    minWidth: 0,
  },
}))
